import Box from '@mui/material/Box';
import { useStacklineTheme } from '@stackline/ui';
import React, { useMemo } from 'react';
import useMarketShareHooks from 'src/components/AtlasV2Components/Hooks/useMarketShareRequestHook';
import KeyMetricCards, {
  KeyMetricCardDefinition
} from 'src/components/BeaconV2Components/KeyMetricCards/KeyMetricCards';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { EnhancedCommonSummaryInfoWithLegend } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import { KEY_METRICS_CONTAINER_ADJUSTED_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { getTrafficV2Treatment, updateUrlQueryParams } from 'src/utils/app';
import { buildSubtitleDisplayName } from 'src/utils/filters';
import { useHistory } from 'src/utils/Hooks';

const titleOverrides = {
  promotions: {
    dataPointCount: 'Promotions'
  },
  traffic: {
    adSpend: 'Ad Spend'
  },
  advertising: {
    clicks: 'Paid Traffic'
  },
  sales: {
    purchaseRate: 'Conversion',
    retailSalesMarketShareV2: 'Share ($)',
    unitsSoldMarketShareV2: 'Share (UNITS)',
    stacklineSku: 'Products'
  },
  reviews: {
    stars: 'Rating'
  },
  buybox: {
    winPercentage: 'Buy Box'
  }
};

/**
 * Shows all the key metric summary cards for the Atlas home page
 */
const AtlasV2KeyMetricsCards = () => {
  const theme = useStacklineTheme();
  const history = useHistory();
  const { requestBuilder, marketShareBuilder } = useMarketShareHooks();

  const trafficV2Treatment = getTrafficV2Treatment();

  // handles some  hard coded logic - copied from Legacy Atlas
  const requestBuilderHandlers: {
    [key: string]: (request: AdvancedSearchRequestBuilder) => AdvancedSearchRequestBuilder;
  } = {
    searchTerm: (request) => request.addConditionRangeFilter('totalClicks', 1),
    reviewsRating: (request) => request.addConditionRangeFilter('reviewsRating', 0.01, 5),
    dataPointCount: (request) =>
      request
        .addConditionRangeFilter('retailSales', 0.01)
        .addSortField('Promotion Count', 'dataPointCount', 'sum', true),
    retailSalesMarketShareV2: (request) => requestBuilder(request),
    unitsSoldMarketShareV2: (request) => requestBuilder(request)
  };

  const cardDefinitions: KeyMetricCardDefinition[][] = useMemo(
    () =>
      [
        ...(trafficV2Treatment === 'both' || trafficV2Treatment === 'on'
          ? [
              [
                ['trafficV2', 'totalClicks', 'trafficV2', 'totalClicks'],
                ['trafficV2', 'organicClicks', 'trafficV2', 'organicClicks'],
                ['trafficV2', 'adClicks', 'trafficV2', 'adClicks'],
                ['trafficV2', 'adSpend', 'trafficV2', 'adClicks'],
                ['trafficV2', 'keywordsWithTotalTrafficCount', 'trafficV2', 'totalClicks']
              ]
            ]
          : []),

        ...(trafficV2Treatment === 'both' || trafficV2Treatment === 'off'
          ? [
              [
                ['traffic', 'totalClicks', 'traffic', 'totalClicks'],
                ['traffic', 'organicClicks', 'traffic', 'organicClicks'],
                ['traffic', 'adClicks', 'traffic', 'adClicks'],
                ['traffic', 'adSpend', 'traffic', 'adClicks'],
                ['traffic', 'searchTerm', 'traffic', 'adClicks']
              ]
            ]
          : []),
        [
          ...(trafficV2Treatment === 'both' || trafficV2Treatment === 'on'
            ? [['trafficV2', 'purchaseRate', 'conversion', 'purchaseRate']]
            : [['sales', 'purchaseRate', 'conversion', 'purchaseRate']]),

          ['sales', 'retailPrice', 'prices', 'retailPrice'],
          ['sales', 'contentScore', 'content', 'contentScore'],
          ['sales', 'reviewsRating', 'rating', 'rating'],
          ['promotions', 'dataPointCount', 'prices', 'promotions']
        ],
        [
          ['sales', 'retailSales', 'sales', 'retailSales'],
          ['sales', 'unitsSold', 'sales', 'unitsSold'],
          ['sales', 'retailSalesMarketShareV2', 'sales', 'retailSales'],
          ['sales', 'unitsSoldMarketShareV2', 'sales', 'unitsSold'],
          ['sales', 'stacklineSku', 'sales', 'retailSales']
        ]
      ].map((columnDefinitions) =>
        columnDefinitions.map(([indexName, fieldName, tab, subtab]) => ({
          indexName,
          fieldName,
          title: titleOverrides[indexName] ? titleOverrides[indexName][fieldName] : undefined,
          useLastValueAggregation: fieldName === 'KeywordsWithPaidTrafficCount',
          shouldSliceByWeekRange: fieldName === 'dataPointCount',
          onClick: () => history.push(updateUrlQueryParams({ tab, subtab })),
          primaryAggregationBuilder: (aggregationBuilder) => {
            if (fieldName === 'reviewsRating') {
              aggregationBuilder.addConditionRangeFilter('reviewsRating', 0.01, 5);
            }

            if (fieldName === 'stacklineSku') {
              aggregationBuilder.addConditionRangeFilter('unitsSold', 4);
            }

            return aggregationBuilder;
          },

          secondaryAggregationBuilder: (aggregationBuilder) => {
            if (fieldName === 'reviewsRating') {
              aggregationBuilder.addConditionRangeFilter('reviewsRating', 0.01, 5);
            }

            if (fieldName === 'stacklineSku') {
              aggregationBuilder.addConditionRangeFilter('unitsSold', 4);
            }

            if (fieldName === 'retailSalesMarketShareV2') {
              aggregationBuilder
                .clearAggregationFields()
                .addAggregationField('Retail Sales', 'retailSales', 'sum', true);
            }

            return aggregationBuilder;
          },
          requestBuilder: requestBuilderHandlers[fieldName]
            ? (request) => requestBuilderHandlers[fieldName](request)
            : undefined,
          marketShareBuilder:
            fieldName === 'retailSalesMarketShareV2' || fieldName === 'unitsSoldMarketShareV2'
              ? (builder) => {
                  return marketShareBuilder(builder);
                }
              : undefined
        }))
      ),
    [history, marketShareBuilder, requestBuilderHandlers]
  );

  return (
    <>
      <EnhancedCommonSummaryInfoWithLegend
        widget={{
          view: {
            buildSubTitle: ({ retailer, mainEntity, filters, categories, app }) =>
              mainEntity ? buildSubtitleDisplayName(retailer, mainEntity, filters, categories, app) : 'Summary',

            title: 'Key Metrics'
          }
        }}
      />
      <Box
        marginTop={theme.spacing.lg}
        marginBottom={theme.spacing.lg}
        sx={{
          transform: 'translateX(-16px)',
          width: KEY_METRICS_CONTAINER_ADJUSTED_WIDTH
        }}
      >
        <KeyMetricCards cardDefinitionsByColumn={cardDefinitions} />
      </Box>
    </>
  );
};

export default AtlasV2KeyMetricsCards;
